import { useEffect, useRef } from "react";

const PALETTES = {
  light: {
    backdrop: "bg-gradient-to-br from-zinc-100 via-zinc-50 to-zinc-200",
    blob1: "radial-gradient(circle, rgba(161,161,170,0.35), rgba(161,161,170,0))",
    blob2: "radial-gradient(circle, rgba(113,113,122,0.28), rgba(113,113,122,0))",
    blob3: "radial-gradient(circle, rgba(212,212,216,0.45), rgba(212,212,216,0))",
    follower2: "radial-gradient(circle, rgba(82,82,91,0.22), rgba(82,82,91,0))",
    follower: "radial-gradient(circle, rgba(161,161,170,0.30), rgba(161,161,170,0))",
    blobBlend: undefined,
  },
  dark: {
    backdropStyle: {
      background: "radial-gradient(ellipse at 50% 30%, #0c0a14 0%, #050507 75%)",
    },
    // Under `mix-blend-mode: screen`, low-alpha violet reads as a "destello",
    // not a wash. Larger blob stays the visible accent; everything else dims.
    blob1: "radial-gradient(circle, rgba(120,90,220,0.32), rgba(120,90,220,0))",
    blob2: "radial-gradient(circle, rgba(70,90,180,0.22), rgba(70,90,180,0))",
    blob3: "radial-gradient(circle, rgba(80,90,120,0.50), rgba(80,90,120,0))",
    follower2: "radial-gradient(circle, rgba(160,120,240,0.18), rgba(160,120,240,0))",
    follower: "radial-gradient(circle, rgba(90,110,200,0.22), rgba(90,110,200,0))",
    blobBlend: "screen",
  },
};

export function AuroraBackground({ theme = "light" }) {
  const followerRef = useRef(null);
  const followerRef2 = useRef(null);

  useEffect(() => {
    const follower = followerRef.current;
    const follower2 = followerRef2.current;
    if (!follower || !follower2) return;

    const target = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const current = { x: target.x, y: target.y };
    const current2 = { x: target.x, y: target.y };

    const onMove = (e) => {
      target.x = e.clientX;
      target.y = e.clientY;
    };

    let raf;
    const tick = () => {
      current.x += (target.x - current.x) * 0.08;
      current.y += (target.y - current.y) * 0.08;
      current2.x += (target.x - current2.x) * 0.035;
      current2.y += (target.y - current2.y) * 0.035;

      follower.style.transform = `translate3d(${current.x - 280}px, ${current.y - 280}px, 0)`;
      follower2.style.transform = `translate3d(${current2.x - 220}px, ${current2.y - 220}px, 0)`;
      raf = requestAnimationFrame(tick);
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    raf = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(raf);
    };
  }, []);

  const p = PALETTES[theme] || PALETTES.light;
  const blobMix = p.blobBlend ? { mixBlendMode: p.blobBlend } : null;

  return (
    <div
      aria-hidden="true"
      className="fixed inset-0 overflow-hidden pointer-events-none"
      style={{ zIndex: -10 }}
    >
      {p.backdropStyle ? (
        <div className="absolute inset-0" style={p.backdropStyle} />
      ) : (
        <div className={`absolute inset-0 ${p.backdrop}`} />
      )}

      <div
        className="aurora-blob aurora-drift-1"
        style={{
          width: 520,
          height: 520,
          left: "-8%",
          top: "-10%",
          background: p.blob1,
          ...blobMix,
        }}
      />
      <div
        className="aurora-blob aurora-drift-2"
        style={{
          width: 420,
          height: 420,
          right: "-6%",
          top: "18%",
          background: p.blob2,
          ...blobMix,
        }}
      />
      <div
        className="aurora-blob aurora-drift-3"
        style={{
          width: 600,
          height: 600,
          left: "20%",
          bottom: "-20%",
          background: p.blob3,
          ...blobMix,
        }}
      />

      <div
        ref={followerRef2}
        className="aurora-blob"
        style={{
          width: 440,
          height: 440,
          left: 0,
          top: 0,
          background: p.follower2,
          ...blobMix,
        }}
      />
      <div
        ref={followerRef}
        className="aurora-blob"
        style={{
          width: 560,
          height: 560,
          left: 0,
          top: 0,
          background: p.follower,
          ...blobMix,
        }}
      />
    </div>
  );
}
